import React from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  Badge,
  ProgressBar,
  Table,
  Button
} from 'react-bootstrap';
import { Link } from 'react-router-dom';

const SuperDistributorDashboard = () => {
  const stats = [
    {
      label: 'Total Distributors',
      value: '1,248',
      delta: '+32',
      deltaType: 'up',
      subtitle: 'vs last month',
      icon: 'bi-people-fill',
      variant: 'primary'
    },
    {
      label: 'Active Retailers',
      value: '12,560',
      delta: '+4.8%',
      deltaType: 'up',
      subtitle: 'active in last 7 days',
      icon: 'bi-shop-window',
      variant: 'success'
    },
    {
      label: 'Monthly Volume',
      value: '₹8.45 Cr',
      delta: '+12.4%',
      deltaType: 'up',
      subtitle: 'collections this month',
      icon: 'bi-arrow-left-right',
      variant: 'info'
    },
    {
      label: 'Commission Earned',
      value: '₹37.6 L',
      delta: '+9.1%',
      deltaType: 'up',
      subtitle: 'projected for month end',
      icon: 'bi-graph-up-arrow',
      variant: 'warning'
    }
  ];

  const walletSummary = [
    {
      title: 'Main Wallet',
      value: '₹2,34,560',
      chip: 'Available',
      variant: 'info',
      icon: 'bi-wallet2'
    },
    {
      title: 'Commission Wallet',
      value: '₹54,820',
      chip: 'Pending Release',
      variant: 'warning',
      icon: 'bi-cash-coin'
    },
    {
      title: 'Security Deposit',
      value: '₹7,50,000',
      chip: 'Locked',
      variant: 'secondary',
      icon: 'bi-shield-lock'
    }
  ];

  const quickActions = [
    {
      title: 'Add Distributor',
      description: 'Create new distributor accounts with onboarding workflows',
      icon: 'bi-person-plus-fill',
      to: '/super-distributor/distributors/create'
    },
    {
      title: 'Approve Wallet Top-up',
      description: 'Review & approve funds requested by distributors',
      icon: 'bi-check-circle-fill',
      to: '/super-distributor/wallet/topups'
    },
    {
      title: 'Commission Planner',
      description: 'Configure slabs, service fees & roll-out schedules',
      icon: 'bi-sliders2-vertical',
      to: '/super-distributor/commission'
    }
  ];

  const networkPerformance = [
    {
      title: 'Distributor Activation',
      current: 78,
      target: 100,
      variant: 'success'
    },
    {
      title: 'Retailer Coverage',
      current: 64,
      target: 90,
      variant: 'info'
    },
    {
      title: 'Service Penetration',
      current: 52,
      target: 75,
      variant: 'warning'
    }
  ];

  const topDistributors = [
    {
      name: 'Rajesh Enterprises',
      region: 'Maharashtra',
      retailers: 186,
      volume: '₹1.12 Cr',
      score: 94
    },
    {
      name: 'Velocity Finserve',
      region: 'Delhi NCR',
      retailers: 152,
      volume: '₹96.4 L',
      score: 89
    },
    {
      name: 'North Star Services',
      region: 'Uttar Pradesh',
      retailers: 210,
      volume: '₹87.3 L',
      score: 88
    },
    {
      name: 'FinGlobe Partners',
      region: 'Karnataka',
      retailers: 134,
      volume: '₹76.9 L',
      score: 84
    }
  ];

  const recentActivities = [
    {
      time: '10:24 AM',
      title: 'Wallet Top-up Approved',
      description: '₹7,00,000 released to Velocity Finserve main wallet',
      status: 'success'
    },
    {
      time: '09:10 AM',
      title: 'Commission Slab Updated',
      description: 'New recharge commission slab effective 15 Nov',
      status: 'warning'
    },
    {
      time: 'Yesterday',
      title: 'Distributor Onboarding',
      description: 'North Star Services completed KYC verification',
      status: 'info'
    },
    {
      time: 'Mon, 04 Nov',
      title: 'Support Escalation Closed',
      description: 'Pending settlement issue resolved for Prism Pay',
      status: 'secondary'
    }
  ];

  const servicePerformance = [
    {
      service: 'AEPS',
      share: 34,
      trend: '+6.2%',
      variant: 'primary'
    },
    {
      service: 'BBPS',
      share: 22,
      trend: '+3.8%',
      variant: 'info'
    },
    {
      service: 'DMT',
      share: 19,
      trend: '+1.4%',
      variant: 'success'
    },
    {
      service: 'Recharge',
      share: 15,
      trend: '-0.8%',
      variant: 'warning'
    }
  ];

  return (
    <div className="super-dashboard">
      <Container fluid className="py-4">
        <div className="dashboard-header mb-4">
          <div>
            <div className="breadcrumb text-muted">
              <span>Home</span> <span className="mx-2">/</span> <span>Super Distributor</span>
            </div>
            <h1 className="dashboard-title mb-1">Super Distributor Control Center</h1>
            <p className="text-muted mb-0">Monitor network health, wallet utilisation and service penetration across zones.</p>
          </div>
          <div className="d-flex flex-column align-items-end gap-2">
            <Badge bg="primary" className="rounded-pill px-3 py-2">
              <i className="bi bi-lightning-charge-fill me-2"></i>
              Live sync: 2 mins ago
            </Badge>
            <Button as={Link} to="/super-distributor/reports" variant="outline-primary" size="sm">
              <i className="bi bi-file-earmark-bar-graph me-1"></i>
              Export Insights
            </Button>
          </div>
        </div>

        <Row className="g-3">
          {stats.map((item, idx) => (
            <Col key={item.label} xl={3} md={6}>
              <Card className="super-stat-card h-100">
                <Card.Body>
                  <div className="d-flex align-items-start justify-content-between">
                    <div>
                      <p className="stat-label text-muted mb-1">{item.label}</p>
                      <h3 className="stat-value mb-1">{item.value}</h3>
                      <span className={`stat-delta ${item.deltaType === 'up' ? 'text-success' : 'text-danger'}`}>
                        <i className={`bi ${item.deltaType === 'up' ? 'bi-arrow-up-right' : 'bi-arrow-down-right'} me-1`}></i>
                        {item.delta}
                      </span>
                      <p className="stat-subtitle text-muted mb-0">{item.subtitle}</p>
                    </div>
                    <div className={`stat-icon bg-${item.variant}`}>
                      <i className={`bi ${item.icon}`}></i>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>

        <Row className="g-3 mt-1">
          <Col xl={8}>
            <Card className="super-network-card h-100">
              <Card.Header className="d-flex justify-content-between align-items-center">
                <div>
                  <h5 className="mb-0">Network Objectives</h5>
                  <small className="text-muted">Tracking progress against monthly targets</small>
                </div>
                <Badge bg="light" text="dark" className="fw-semibold">
                  November Target Cycle
                </Badge>
              </Card.Header>
              <Card.Body>
                <Row className="g-4">
                  {networkPerformance.map((item) => (
                    <Col md={4} key={item.title}>
                      <div className="super-progress-item">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <h6 className="mb-0">{item.title}</h6>
                          <span className="text-muted">{item.current}%</span>
                        </div>
                        <ProgressBar now={item.current} variant={item.variant} className="progress-sm" />
                        <div className="d-flex justify-content-between align-items-center mt-2 text-muted">
                          <small>Goal {item.target}%</small>
                          <small>{item.current >= item.target ? 'On track' : `${item.target - item.current}% gap`}</small>
                        </div>
                      </div>
                    </Col>
                  ))}
                </Row>

                <div className="d-flex align-items-center justify-content-between bg-light rounded-3 px-3 py-3 mt-3">
                  <div>
                    <h6 className="mb-1">Actionable Insight</h6>
                    <p className="mb-0 text-muted">48 distributors have low AEPS adoption. Trigger enablement campaign?</p>
                  </div>
                  <Button variant="primary" size="sm">
                    Run Campaign
                  </Button>
                </div>
              </Card.Body>
            </Card>
          </Col>

          <Col xl={4}>
            <Card className="super-wallet-card h-100">
              <Card.Header>
                <h5 className="mb-0">Wallet & Liquidity</h5>
              </Card.Header>
              <Card.Body className="d-flex flex-column gap-3">
                {walletSummary.map((wallet) => (
                  <div key={wallet.title} className="wallet-item">
                    <div className="wallet-icon">
                      <i className={`bi ${wallet.icon}`}></i>
                    </div>
                    <div className="wallet-content">
                      <div className="d-flex justify-content-between align-items-start">
                        <div>
                          <h6 className="mb-1">{wallet.title}</h6>
                          <p className="mb-0 text-muted">{wallet.value}</p>
                        </div>
                        <Badge bg={wallet.variant} className="rounded-pill">
                          {wallet.chip}
                        </Badge>
                      </div>
                    </div>
                  </div>
                ))}
                <div className="wallet-footer bg-primary-subtle text-primary rounded-3 px-3 py-2">
                  <i className="bi bi-lightbulb-fill me-2"></i>
                  Auto-balancing enabled for 5 high-volume distributors.
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Row className="g-3 mt-1">
          <Col lg={8}>
            <Card className="super-table-card h-100">
              <Card.Header className="d-flex justify-content-between align-items-center">
                <h5 className="mb-0">Top Performing Distributors</h5>
                <Link to="/super-distributor/distributors" className="text-primary fw-semibold">
                  View All
                  <i className="bi bi-arrow-right-short ms-1"></i>
                </Link>
              </Card.Header>
              <Card.Body className="p-0">
                <Table responsive hover className="mb-0">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Region</th>
                      <th>Retailers</th>
                      <th>Monthly Volume</th>
                      <th className="text-end">Health Score</th>
                    </tr>
                  </thead>
                  <tbody>
                    {topDistributors.map((dist) => (
                      <tr key={dist.name}>
                        <td>
                          <div className="d-flex align-items-center gap-2">
                            <div className="dist-avatar">
                              {dist.name
                                .split(' ')
                                .map((word) => word.charAt(0))
                                .join('')
                                .substring(0, 2)}
                            </div>
                            <div>
                              <span className="fw-semibold">{dist.name}</span>
                              <small className="d-block text-muted">Premium Tier</small>
                            </div>
                          </div>
                        </td>
                        <td>{dist.region}</td>
                        <td>{dist.retailers}</td>
                        <td>{dist.volume}</td>
                        <td className="text-end">
                          <Badge bg={dist.score > 90 ? 'success' : 'info'} className="px-3 py-2">
                            {dist.score}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          </Col>

          <Col lg={4}>
            <Card className="super-service-card h-100">
              <Card.Header>
                <h5 className="mb-0">Service Contribution</h5>
                <small className="text-muted">Share of monthly volume</small>
              </Card.Header>
              <Card.Body className="d-flex flex-column gap-3">
                {servicePerformance.map((service) => (
                  <div key={service.service} className="service-item">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <h6 className="mb-0">{service.service}</h6>
                      <span className="text-muted">{service.share}%</span>
                    </div>
                    <ProgressBar now={service.share} variant={service.variant} className="progress-xs" />
                    <div className="d-flex justify-content-between mt-1 text-muted">
                      <small>Contribution</small>
                      <small className={service.trend.startsWith('+') ? 'text-success' : 'text-danger'}>
                        {service.trend}
                      </small>
                    </div>
                  </div>
                ))}
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Row className="g-3 mt-1">
          <Col xl={5}>
            <Card className="super-actions-card h-100">
              <Card.Header>
                <h5 className="mb-0">Quick Actions</h5>
              </Card.Header>
              <Card.Body className="d-flex flex-column gap-3">
                {quickActions.map((action) => (
                  <Link key={action.title} to={action.to} className="quick-action">
                    <div className="action-icon">
                      <i className={`bi ${action.icon}`}></i>
                    </div>
                    <div>
                      <h6 className="mb-1">{action.title}</h6>
                      <p className="mb-0 text-muted">{action.description}</p>
                    </div>
                    <i className="bi bi-arrow-right-short ms-auto"></i>
                  </Link>
                ))}
              </Card.Body>
            </Card>
          </Col>

          <Col xl={7}>
            <Card className="super-timeline-card h-100">
              <Card.Header className="d-flex justify-content-between align-items-center">
                <div>
                  <h5 className="mb-0">Recent Activities</h5>
                  <small className="text-muted">Audit trail for the last 48 hours</small>
                </div>
                <Button variant="outline-secondary" size="sm">
                  View Logs
                </Button>
              </Card.Header>
              <Card.Body>
                <div className="super-timeline">
                  {recentActivities.map((activity, index) => (
                    <div key={`${activity.title}-${index}`} className="timeline-item">
                      <div className={`timeline-dot ${activity.status}`}></div>
                      <div className="timeline-content">
                        <div className="d-flex justify-content-between align-items-center">
                          <h6 className="mb-0">{activity.title}</h6>
                          <span className="text-muted small">{activity.time}</span>
                        </div>
                        <p className="mb-0 text-muted">{activity.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default SuperDistributorDashboard;


